import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "./use-auth";
import { useLocalStorage } from "./use-local-storage";
import { useTemplates } from "./use-templates";
import { SCRIBENOTE_DEFAULTS } from "../components/common/record/constants";

const RecordingJobTypeContext = createContext();

const DEFAULT_JOB_TYPE = {
  value: "medical_record", // The will be a UUID for templates
  name: "Medical Record",
  icon: faSoapNote,
  description:
    "Select 'Medical Record' to generate an automatic SOAP note for your patient.",
};

// For defaults, compare storedValue to option.value;
// For custom templates, compare storedValue to template.uuid.
function getMatchingJobType(storedValue, autoTemplates) {
  let match = SCRIBENOTE_DEFAULTS.find(
    (option) => option.value === storedValue,
  );
  if (!match && autoTemplates && Array.isArray(autoTemplates)) {
    const templateMatch = autoTemplates.find(
      (template) => template.uuid === storedValue,
    );
    if (templateMatch) {
      match = {
        value: templateMatch.uuid,
        name: templateMatch.name,
        description: templateMatch.description,
        templateType: templateMatch.templateType,
        icon: templateMatch.icon || faFile,
      };
    }
  }
  return match;
}

export const RecordingJobTypeProvider = ({ children }) => {
  const [filterJobType, setFilterJobType] = useLocalStorage(
    "filter-job-type",
    null,
  );
  const auth = useAuth();
  const { allTemplates } = useTemplates();

  // Derive the job type from localStorage + loaded templates.
  // Recalculates when allTemplates loads/changes, resolving template UUIDs
  // that can't be matched until the query completes.
  const storageJobType = useMemo(() => {
    const storedValue = localStorage.getItem("recordingJobType");
    if (storedValue) {
      return (
        getMatchingJobType(storedValue, allTemplates || []) ||
        DEFAULT_JOB_TYPE
      );
    }
    return DEFAULT_JOB_TYPE;
  }, [allTemplates]);

  // Tracks explicit user selections within the session. When null, we
  // fall back to the storage-derived value above.
  const [userOverride, setUserOverride] = useState(null);

  // Derive selectedJobType: use the override only if it still refers to
  // a valid template/default in the current allTemplates list. Otherwise
  // fall back to storageJobType (which already validates against allTemplates).
  const selectedJobType = useMemo(() => {
    if (!userOverride) return storageJobType;
    const stillValid = getMatchingJobType(
      userOverride.value,
      allTemplates || [],
    );
    return stillValid ? userOverride : storageJobType;
  }, [userOverride, allTemplates, storageJobType]);

  const setSelectedJobType = (jobType) => {
    setUserOverride(jobType);
    if (jobType) {
      localStorage.setItem("recordingJobType", jobType.value);
    }
  };

  const resetSelectedJobType = () =>
    setSelectedJobType(DEFAULT_JOB_TYPE);

  // Reset job type when the user becomes logged out
  useEffect(() => {
    if (!auth.isUserAuthenticated) {
      setFilterJobType(null);
    }
  }, [auth.isUserAuthenticated, setFilterJobType]);

  return (
    <RecordingJobTypeContext.Provider
      value={{
        selectedJobType,
        setSelectedJobType,
        resetSelectedJobType,
        filterJobType,
        setFilterJobType,
      }}
    >
      {children}
    </RecordingJobTypeContext.Provider>
  );
};

export const useRecordingJobType = () => {
  const context = useContext(RecordingJobTypeContext);
  if (!context) {
    throw new Error(
      "useRecordingJobType must be used within a RecordingJobTypeProvider",
    );
  }
  return context;
};
